import useSetFilterBoundaries from "@SearchResults/features/sidebar/hooks/useSetFilterBoundaries";
import useSetMaxRoutePrice from "@SearchResults/features/banners/hooks/useSetMaxRoutePrice";
import useSetRouteCounts from "@SearchResults/hooks/useSetRouteCounts";
import {useCallback, useEffect} from "react";
import CurrencyType from "@Currency/types/Currency.type";
import RouteWithAddedPropsAndPricesType from "@Route/types/RouteWithAddedPropsAndPrices.type";
import RouteType from "@Route/types/Route.type";
import useClearAllFilters from "@SearchResults/features/sidebar/hooks/useClearAllFilters";
import useSetDefaultFilters from "@SearchResults/features/sidebar/hooks/useSetDefaultFilters";

type SideEffectParams = {
    preparedRoutes: RouteWithAddedPropsAndPricesType[],
    allPreparedRoutes: RouteWithAddedPropsAndPricesType[],
    allRoutes: RouteType[],
    isPastDeparture: boolean,
    searchDate: string,
    currency: CurrencyType,
}

/**
 * This hook invokes various side effects on page which cause full re-renders as they propagated via redux
 * Must be explicitly used with a callback as otherwise it would cause render infinite loop
 */
export default function useRoutesListSideEffects(sideEffectParams: SideEffectParams) {
    const {
        preparedRoutes,
        allPreparedRoutes,
        allRoutes,
        searchDate,
        isPastDeparture,
        currency,
    } = sideEffectParams

    const {setFilterLimits} = useSetFilterBoundaries(searchDate)
    const {setMaxRoutePrice} = useSetMaxRoutePrice()
    const {setRouteCounts} = useSetRouteCounts()
    const {clearFilters} = useClearAllFilters()
    const {setDefaultFilters} = useSetDefaultFilters()

    const updateSideEffectValues = useCallback(() => {
        if (!isPastDeparture) {
            setFilterLimits(allPreparedRoutes)
            setMaxRoutePrice(preparedRoutes)
        }
        // Only auto-clear filters if there are very few routes
        if (allRoutes.length <= 3 && preparedRoutes.length <= 1) {
            clearFilters()
        }
        // Don't auto-set default filters - let user control their filters
        // if (allRoutes.length > 10) {
        //     setDefaultFilters()
        // }
    }, [
        preparedRoutes.length,
        allRoutes.length,
        currency,
    ])

    useEffect(updateSideEffectValues, [allRoutes.length, currency])

    useEffect(() => {
        setRouteCounts(allRoutes.length, preparedRoutes.length)
    }, [preparedRoutes.length, allRoutes.length])
}